import * as React from "react";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import {
  CheckBox,
  CheckBoxOutlineBlank,
  CheckCircle,
  RadioButtonUnchecked,
  NotInterested,
} from "@material-ui/icons";

// internal
import { styles } from "./LargeControlGroup.styles";

export interface ControlData {
  on: boolean;
  disabled?: boolean;
}

export interface IProps<D extends ControlData> extends WithStyles<typeof styles> {
  controlType: "checkbox" | "radio";
  controls: D[];
  onChange: (controlIndex: number, control: D) => void;
  renderControlContent: (d: D) => JSX.Element | null;
  singular?: boolean;
  horizontal?: boolean;
  overrideContent?: boolean;
  allowPointerEvents?: boolean;
}

export interface IState {
  singular: boolean;
}

class LargeControlGroupComponent<D extends ControlData> extends React.Component<IProps<D>, IState> {
  static defaultProps = {
    controlType: "radio" as "radio",
  };

  constructor(props: IProps<D>) {
    super(props);

    this.state = {
      singular: props.singular !== undefined ? props.singular : props.controlType === "radio",
    };
  }

  ///////////////
  // Lifecycle //
  ///////////////
  public componentDidMount() {
    if (this.state.singular) {
      const onControls = this.props.controls.map((d, i) => ({ d, i })).filter(({ d }) => d.on);

      onControls.slice(1).forEach(({ d, i }) => this.props.onChange(i, { ...d, on: false }));
    }
  }

  ///////////////
  // Rendering //
  ///////////////
  private getControlIcon = (control: D) => {
    const { controlType } = this.props;

    if (control.disabled) {
      return NotInterested;
    }

    switch (controlType) {
      case "checkbox":
        return control.on ? CheckBox : CheckBoxOutlineBlank;
      case "radio":
      default:
        return control.on ? CheckCircle : RadioButtonUnchecked;
    }
  };

  private onChange = (index: number, control: D) => {
    if (this.state.singular) {
      const newControl = {
        ...control,
        on: true,
      };

      this.props.onChange(index, newControl);

      this.props.controls
        .map((d, i) => ({ d, i }))
        .filter(({ d, i }) => d.on && i !== index)
        .forEach(({ d, i }) => this.props.onChange(i, { ...d, on: false }));
    } else {
      this.props.onChange(index, { ...control, on: !control.on });
    }
  };

  private renderControl = (control: D, index: number) => {
    const { classes, overrideContent, allowPointerEvents } = this.props;

    const Icon = this.getControlIcon(control);

    return (
      <div
        key={`control-${index}`}
        className={`${classes.control} ${control.on ? "on" : ""} ${
          control.disabled ? classes.disabled : ""
        }`}
        style={overrideContent ? { padding: 0 } : undefined}
      >
        <div
          className={`${classes.controlContent} ${
            control.disabled && !allowPointerEvents ? classes.pointerEventsNone : ""
          }`}
          onClick={() => this.onChange(index, control)}
        >
          {!overrideContent ? <Icon /> : ""}
          {this.props.renderControlContent(control)}
        </div>
      </div>
    );
  };

  public render() {
    const { controls, horizontal, classes } = this.props;

    return (
      <div className={horizontal ? classes.horizontal : undefined}>
        {controls.map(this.renderControl)}
      </div>
    );
  }
}

/////////////////
// With Styles //
/////////////////

export const LargeControlGroup = withStyles(styles)(LargeControlGroupComponent);
